import { useEffect, useRef, useState } from "react";
import { verifyEmailOtp } from "../services/auth";
import { getApiErrorMessage } from "../utils/Util";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  verificationId: string;
  onSuccess: () => void;
  modalId?: string;
}

function EmailVerificationPopup({ verificationId, onSuccess, modalId = "emailVerificationModal" }: Props) {
  const { t } = useLanguage();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const modalEl = document.getElementById(modalId);
    if (!modalEl) return;
    const reset = () => { setCode(""); setError(""); };
    modalEl.addEventListener("show.bs.modal", reset);
    return () => modalEl.removeEventListener("show.bs.modal", reset);
  }, [modalId]);

  const handleVerify = async () => {
    if (code.length !== 6 || loading) return;
    setError("");
    setLoading(true);
    try {
      await verifyEmailOtp(verificationId, code);
      closeRef.current?.click();
      onSuccess();
    } catch (err) {
      setError(getApiErrorMessage(err, t("emailVerification.error")));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.slice(0, 6));
    if (error) setError("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && code.length === 6) handleVerify();
  };

  return (
    <div
      className="modal fade"
      id={modalId}
      tabIndex={-1}
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "360px" }}>
        <div className="modal-content rounded-4 shadow">
          <button
            ref={closeRef}
            type="button"
            className="btn-close position-absolute top-0 end-0 m-3"
            data-bs-dismiss="modal"
            aria-label={t("common.close")}
            style={{ zIndex: 1 }}
          />

          <div className="modal-body px-4 py-4 text-center">
            <div className="mb-2">
              <i className="bi bi-envelope-check-fill" style={{ fontSize: "3rem", color: "#c20e1a" }}></i>
            </div>

            <h6 className="fw-bold fs-5 mb-1">{t("emailVerification.title")}</h6>
            <p className="text-muted small mb-4">
              {t("emailVerification.subtitle")}
            </p>

            <input
              type="text"
              className={`form-control text-center fw-bold fs-4 mb-4${error ? " border-danger" : code.length === 6 ? " border-success" : ""}`}
              style={{
                letterSpacing: "0.4em",
                borderRadius: "10px",
                boxShadow: error
                  ? "0 0 0 2px rgba(220,53,69,0.2)"
                  : code.length === 6
                  ? "0 0 0 2px rgba(25,135,84,0.2)"
                  : undefined,
              }}
              maxLength={6}
              placeholder="······"
              value={code}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="one-time-code"
            />

            {loading && (
              <div className="mb-3">
                <span className="spinner-border spinner-border-sm text-danger me-2" />
                <span className="text-muted small">{t("otp.verifying")}</span>
              </div>
            )}

            {error && (
              <div className="alert alert-danger py-2 small mb-3">
                <i className="bi bi-exclamation-circle me-1"></i>
                {error}
              </div>
            )}

            <button
              type="button"
              className="btn btn-danger w-100 rounded-pill py-2 fw-semibold mb-3"
              onClick={handleVerify}
              disabled={code.length !== 6 || loading}
            >
              <i className="bi bi-check-circle me-2"></i>
              {t("common.verify")}
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm w-100 rounded-pill"
              data-bs-dismiss="modal"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailVerificationPopup;
